import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { StorageService } from './storage.service';
import { OktaService } from './okta.service';
import { HttpService } from './http.service';
import { mutin_spcer } from './window-payload-builder.service';
import {
  calculateWindowArea,
  calculateFrameArea,
  calculateGlassEdge,
  calculateMuntinLength,
  extractFrameWidths,
  WindowDimensions
} from '../utils/calculation';
import { environment } from 'src/environments/environment';
import { catchError, defer, map, mergeMap, Observable, retryWhen, throwError, timer } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ConfigurationApiService {

  private readonly RETRY_COUNT = 2;
  private readonly BASE_DELAY_MS = 200;
  constructor(
    private storageService: StorageService,
    private oktaService: OktaService,
    private httpService: HttpService
  ) { }

  // ─────────────────────────────────────────────
  // PUBLIC
  // ─────────────────────────────────────────────
  async saveConfiguration(projectName: string, overwriteProject?: boolean): Promise<any> {
    console.log()
    const projectRaw = await this.storageService.getProject();
    const compRaw = await this.storageService.getComparedProject();
    const p = projectRaw ? JSON.parse(projectRaw) : null;
    const p1 = p;
    const p2 = compRaw ? JSON.parse(compRaw) : null;
    if (!p) throw new Error('No project data in storage');

    const userClaims = await this.oktaService.userInfo();
    const userId = (userClaims?.['sub'] as string) ?? '';

    const payload: any = {
      userId,
      projectName,
      overwriteProject,
      windowConfiguration: this.buildWindowConfiguration(p1, projectName),
      configurationResult: this.buildConfigurationResult(p)
    };
    if (p.id) {
      payload.id = p.id;
    }
    if (this.hasEnergySavings(p)) {
      payload.windowConfiguration2 = this.buildWindowConfiguration(p2, projectName);
    }

    console.log('Save configuration payload:', JSON.stringify(payload, null, 2));
    return this.postConfiguration(payload);
  }

  // ─────────────────────────────────────────────
  // Read raw project from Capacitor Preferences
  // ─────────────────────────────────────────────
  private async getRawProject(key: string): Promise<any> {
    try {
      const { value } = await Preferences.get({ key });
      return value ? JSON.parse(value) : null;
    } catch {
      return null;
    }
  }

  // ─────────────────────────────────────────────
  // windowConfiguration
  // ─────────────────────────────────────────────
  private buildWindowConfiguration(p: any, projectName: string): any {
    if (!p) return null;

    const saveOnlyGlass = p?.saveOnlyGlass || false;

    // If saveOnlyGlass is true, only return glass and projectName
    if (saveOnlyGlass) {
      const airResult = p.airSimulationResult;
      const glass = {
        ...p.windowglas,
        ...(airResult?.shortEdge != null ? { shortEdge: airResult.shortEdge } : {}),
        ...(airResult?.longEdge != null ? { longEdge: airResult.longEdge } : {}),
      };

      return {
        projectName,
        saveOnlyGlass,
        glass
      };
    }

    // Full configuration for normal case
    const frame = p.windowframe || {};
    const dimensions: WindowDimensions = {
      a: parseFloat(p.dimensions?.a || 0),
      b: parseFloat(p.dimensions?.b || 0),
      c: parseFloat(p.dimensions?.c || 0)
    };
    const windowTypeId = parseInt(p.windowtype || 1);
    const frameWidths = extractFrameWidths(frame);
    const muntinType = { id: parseInt(p.muntin?.type?.id || 0) };
    const muntinShape = {
      id: parseInt(p.muntin?.shape?.id || 0),
      countHorizontal: parseInt(p.muntin?.shape?.countHorizontal || 0),
      countVertical: parseInt(p.muntin?.shape?.countVertical || 0)
    };

    // ── calculate all areas first ──
    const windowArea = calculateWindowArea(windowTypeId, dimensions);
    const frameArea = calculateFrameArea(windowTypeId, dimensions, frameWidths);
    const glassArea = windowArea - frameArea;
    const glassEdge = calculateGlassEdge(windowTypeId, dimensions, frameWidths);
    const muntinLength = calculateMuntinLength(windowTypeId, dimensions, frameWidths, muntinType, muntinShape);
    const airResult = p.airSimulationResult;
    const glass = {
      ...p.windowglas,
      ...(airResult?.shortEdge != null ? { shortEdge: airResult.shortEdge } : {}),
      ...(airResult?.longEdge != null ? { longEdge: airResult.longEdge } : {}),
    };

    return {
      projectName,
      saveOnlyGlass,
      frame: p.windowframe,
      glass,
      spacer: p.spacer,
      spacerExpert: p.spacerExpert ?? null,
      muntin: p.muntin,
      windowType: p.windowtype ?? 1,
      dimensions: p.dimensions,
      windowArea,
      frameArea,
      glassArea,
      glassEdge,
      muntinLength,
      includeCorrectionPsiForCavities: p?.glassCorrectionFactor ?? false
    };
  }

  // ─────────────────────────────────────────────
  // configurationResult
  // ─────────────────────────────────────────────
  private buildConfigurationResult(p: any): any {
    const saveOnlyGlass = p?.saveOnlyGlass ?? false;
    const air = p.airSimulationResult ?? null;

    const airSimulationResult = air
      ? {
        success: true,
        errorMessage: '',
        lifetimeOk: air.lifetimeResult !== 'not calculated',
        transportOk: air.transportResult !== 'not calculated',
        lifeTime: {
          installationPlace: p.lifetime?.installationPlace ?? '',
          latitude: String(p.lifetime?.latitude ?? ''),
          longitude: String(p.lifetime?.longitude ?? ''),
          rating: String(air.lifetimeResult ?? '')
        },
        elevatedTransport: {
          meanOfTransport: p.elevatedTransport?.transport ?? '',
          maxHeightDifference: String(p.elevatedTransport?.maxHeightDiff ?? ''),
          rating: String(air.transportResult ?? '')
        }
      }
      : null;

    return {
      mainResult: saveOnlyGlass ? 0 : p.uw ?? 0,
      psiResult: saveOnlyGlass ? null : p.psi ?? 0,
      condensationResult: saveOnlyGlass ? null : p.comfortCalculator ?? null,
      energySavingResult: saveOnlyGlass ? null : p.energySavingsResult ?? null,
      windowEnergyRatingResult: saveOnlyGlass ? null : p.windowEnergyRatingResult ?? null,
      airSimulationResult
    };
  }

  // ─────────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────────
  private hasEnergySavings(p: any): boolean {
    const r = p?.energySavingsResult;
    if (!r) return false;
    return !!(r.wiinterRsult || r.summerResult || r.summerWinterResult);
  }

  private handleSuccessResponse(response: any): any {
    if (response.status < 200 || response.status >= 300) throw response;
    return response.data;
  }
  // ─────────────────────────────────────────────
  // RETRY
  // ─────────────────────────────────────────────
  private retryWithBackoff() {
    return (errors: Observable<any>) =>
      errors.pipe(
        mergeMap((error, retryIndex) => {
          const attempt = retryIndex + 1;
          if (attempt > this.RETRY_COUNT || !this.isRetryableError(error?.status)) {
            return throwError(() => error);
          }
          return timer(Math.pow(2, attempt) * this.BASE_DELAY_MS);
        })
      );
  }

  private isRetryableError(status?: number): boolean {
    return [0, 408, 429, 500, 502, 503, 504].includes(status ?? 0);
  }

  private handleFinalError(error: any): Error {
    if (typeof error?.status === 'number') return this.handleHttpError(error.status, error.data);
    return this.handleNetworkError(error);
  }

  private handleHttpError(status: number, data: any): Error {
    const messageMap: Record<number, string> = {
      400: 'Invalid request parameters.',
      401: 'Your session expired. Please sign in again.',
      403: 'Access forbidden.',
      404: 'Config not found.',
      500: 'Server error. Please try again later.',
      502: 'Service temporarily unavailable.',
      503: 'Service temporarily unavailable.',
      504: 'Service temporarily unavailable.',
    };
    const message = data?.message || messageMap[status] || `Error ${status}`;
    console.error('API Error:', status, data);
    return new Error(message);
  }

  private handleNetworkError(error: any): Error {
    const message = error?.message?.includes('timeout')
      ? 'Request timeout. Please try again.'
      : 'Network error. Please check your connection.';
    console.error('Network Error:', error);
    return new Error(message);
  }
  // private async getAccessToken(): Promise<string> {
  //   try {
  //     const oktaAuth = (this.oktaService as any).oktaAuth;
  //     const tokenObj = await oktaAuth?.tokenManager?.get('accessToken');
  //     return tokenObj?.accessToken ?? '';
  //   } catch { return ''; }
  // }

  // ─────────────────────────────────────────────
  // HTTP
  // ─────────────────────────────────────────────
  private async postConfiguration(payload: any): Promise<any> {
    console.log("Call API Configs", payload);
    const url = environment.caluwin_api.config_base_url + environment.caluwin_api.endpoints.saveConfiguration;
    const headers = {
      'Content-Type': 'application/json',
    };
    const response = await this.httpService.executeWithAuthPolicy('POST', { url, headers, data: payload });
    console.log('Save config response:', response.status, response.data);
    if (response.status >= 200 && response.status < 300) {
      this.storageService.setConfigStatus('SAVED');
      return response.data;
    }
    throw { status: response.status, message: response.status.toString(), data: response.data };
  }

  deleteConfiguration(): Observable<any> {
    const projectRaw = this.storageService.getProject();
    const p = projectRaw ? JSON.parse(projectRaw) : null;
    const id = p?.id;

    if (!id) return throwError(() => new Error('No saved config id found in storage'));

    const baseUrl = environment.caluwin_api.config_base_url
      + environment.caluwin_api.endpoints.deleteConfiguration.replace('{id}', id);
    const headers = {
      'Content-Type': 'application/json',
    };
    return defer(async () => {
      const userClaims = await this.oktaService.userInfo();
      const userId = (userClaims?.['sub'] as string) ?? '';
      const url = `${baseUrl}?userId=${encodeURIComponent(userId)}`;

      return this.httpService.executeWithAuthPolicy('DELETE', { url, headers });
    }).pipe(
      map(this.handleSuccessResponse),
      retryWhen(this.retryWithBackoff()),
      catchError(err => throwError(() => this.handleFinalError(err)))
    );
  }

}
